import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Mail, Phone, Globe, Monitor, Smartphone,
  Clock, MapPin, Eye, MessageSquare, RefreshCw, ExternalLink,
  Activity, Calendar, MousePointer, FileText, ChevronRight,
  Zap, Target
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatVisitorDetail = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { visitorId } = useParams();
  const [visitor, setVisitor] = useState(null);
  const [pageEvents, setPageEvents] = useState([]);
  const [chats, setChats] = useState([]);
  const [linkedLead, setLinkedLead] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (visitorId) {
      fetchVisitorDetails();
    }
  }, [visitorId, token]);

  const fetchVisitorDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/live-chat/visitors/${visitorId}?token=${token}`);
      
      if (response.data.success) {
        setVisitor(response.data.visitor);
        setPageEvents(response.data.page_events || []);
        setChats(response.data.chats || []);
        setLinkedLead(response.data.linked_lead);
      }
    } catch (error) {
      toast.error('Failed to load visitor details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const startChatWithVisitor = () => {
    navigate(`/marketing/live-chat/chats/new?visitor_id=${visitorId}`);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'mobile') return <Smartphone size={16} />;
    return <Monitor size={16} />;
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!visitor) {
    return (
      <div className="text-center py-12">
        <User size={48} className="mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Visitor not found</h3>
        <Button variant="outline" onClick={() => navigate('/marketing/live-chat/visitors')}>
          Back to Visitors
        </Button>
      </div>
    );
  }

  const isOnline = visitor.last_seen_at && 
    (new Date() - new Date(visitor.last_seen_at)) < 5 * 60 * 1000;

  return (
    <div className="space-y-6" data-testid="visitor-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/live-chat/visitors')}>
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar className="w-12 h-12">
                <AvatarFallback className="bg-slate-200 text-slate-600">
                  {visitor.is_identified 
                    ? (visitor.visitor_name || visitor.visitor_email || 'V')[0].toUpperCase()
                    : '?'}
                </AvatarFallback>
              </Avatar>
              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${
                isOnline ? 'bg-emerald-500' : 'bg-slate-300'
              }`} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                {visitor.visitor_name || visitor.visitor_email || 'Anonymous Visitor'}
              </h1>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <span className={`flex items-center gap-1 ${isOnline ? 'text-emerald-600' : ''}`}>
                  <Activity size={12} /> {isOnline ? 'Online now' : 'Offline'}
                </span>
                {visitor.is_returning && (
                  <Badge variant="outline">Returning Visitor</Badge>
                )}
                {visitor.is_identified && (
                  <Badge className="bg-emerald-50 text-emerald-700">Identified</Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchVisitorDetails}>
            <RefreshCw size={14} className="mr-2" /> Refresh
          </Button>
          <Button onClick={startChatWithVisitor} className="gap-2">
            <MessageSquare size={14} /> Start Chat
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Visitor Info */}
        <Card className="p-5 space-y-5 border-slate-200">
          <h3 className="font-medium text-slate-900">Visitor Information</h3>
          
          <div className="space-y-4">
            {visitor.visitor_email && (
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-sm">{visitor.visitor_email}</p>
                </div>
              </div>
            )}
            
            {visitor.visitor_phone && (
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="text-sm">{visitor.visitor_phone}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              {getDeviceIcon(visitor.device_type)}
              <div>
                <p className="text-xs text-slate-500">Device</p>
                <p className="text-sm">{visitor.device_type || 'Desktop'} / {visitor.browser || 'Unknown'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Monitor size={16} className="text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Operating System</p>
                <p className="text-sm">{visitor.os || 'Unknown'}</p>
              </div>
            </div>
            
            {visitor.country && (
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="text-sm">{visitor.city ? `${visitor.city}, ` : ''}{visitor.country}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="pt-4 border-t border-slate-100 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">First Seen</span>
              <span>{formatDateTime(visitor.first_seen_at)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Last Activity</span>
              <span>{formatDateTime(visitor.last_seen_at)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Total Visits</span>
              <span>{visitor.visit_count || 1}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Pages Viewed</span>
              <span>{visitor.page_views || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Time on Site</span>
              <span>{formatDuration(visitor.total_time_seconds)}</span>
            </div>
          </div>
          
          {linkedLead && (
            <div className="pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">Linked Lead</p>
              <div 
                className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100"
                onClick={() => navigate(`/contacts/${linkedLead.id}`)}
              >
                <Target size={16} className="text-emerald-600" />
                <div className="flex-1">
                  <p className="font-medium text-emerald-900">{linkedLead.name}</p>
                  <p className="text-xs text-emerald-700">{linkedLead.email}</p>
                </div>
                <ChevronRight size={16} className="text-emerald-600" />
              </div>
            </div>
          )}
        </Card>

        {/* Right Column - Activity */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="journey" className="space-y-4">
            <TabsList>
              <TabsTrigger value="journey">Page Journey</TabsTrigger>
              <TabsTrigger value="chats">Chat History ({chats.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="journey">
              <Card className="border-slate-200">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe size={16} className="text-slate-500" />
                      <span className="font-medium text-slate-900">Current Page</span>
                    </div>
                    {isOnline && (
                      <Badge className="bg-emerald-50 text-emerald-700">Live</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1 truncate">{visitor.current_page || 'Unknown'}</p>
                </div>
                
                <ScrollArea className="h-[400px]">
                  <div className="p-4 space-y-1">
                    {pageEvents.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-500">No page events recorded</p>
                      </div>
                    ) : (
                      <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-slate-200" />
                        
                        {pageEvents.map((event, idx) => (
                          <div key={event.id || idx} className="relative flex gap-4 pb-4">
                            {/* Timeline dot */}
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                              event.event_type === 'page_view' 
                                ? 'bg-blue-100 text-blue-600' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {event.event_type === 'page_view' ? (
                                <Eye size={10} />
                              ) : (
                                <MousePointer size={10} />
                              )}
                            </div>
                            
                            {/* Event content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-slate-900">
                                  {event.event_type === 'page_view' ? 'Viewed Page' : event.event_type}
                                </p>
                                <span className="text-xs text-slate-400">{formatTime(event.created_at)}</span>
                              </div>
                              <p className="text-sm text-slate-500 truncate" title={event.page_url}>
                                {event.page_url?.replace(/^https?:\/\/[^/]+/, '') || '/'}
                              </p>
                              {event.page_title && (
                                <p className="text-xs text-slate-400 mt-0.5">{event.page_title}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </Card>
            </TabsContent>
            
            <TabsContent value="chats">
              <Card className="border-slate-200 p-4">
                {chats.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare size={40} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 mb-4">No chat conversations yet</p>
                    <Button onClick={startChatWithVisitor} variant="outline">
                      Start a Conversation
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chats.map(chat => (
                      <div 
                        key={chat.id}
                        className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/marketing/live-chat/chats/${chat.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <MessageSquare size={16} className="text-slate-400" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              Chat #{chat.id.slice(0, 8)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatDateTime(chat.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${
                            chat.status === 'resolved' ? 'bg-emerald-50 text-emerald-700' :
                            chat.status === 'open' ? 'bg-blue-50 text-blue-700' :
                            ''
                          }`}>
                            {chat.status}
                          </Badge>
                          <ChevronRight size={16} className="text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default LiveChatVisitorDetail;
